import React, { useState, useEffect } from 'react';
import axios from 'axios';
import showToast from '../../utils/toastHelper';
import ConfirmModal from '../../components/ConfirmModal';

const Groups = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [formData, setFormData] = useState({ name: '', type: 'class' });
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendGroup, setSendGroup] = useState(null);
  const [sendElection, setSendElection] = useState('');
  const [elections, setElections] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);

  useEffect(() => {
    fetchGroups();
    fetchElections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filterType ? `?type=${filterType}` : '';
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/groups${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroups(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      showToast('Failed to load groups', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');

      if (editingGroup) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/groups/${editingGroup.id}`,
          { name: formData.name },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showToast('Group updated successfully', 'success');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/groups`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showToast('Group created successfully', 'success');
      }

      setShowAddModal(false);
      setEditingGroup(null);
      setFormData({ name: '', type: 'class' });
      fetchGroups();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save group', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (group) => {
    setEditingGroup(group);
    setFormData({ name: group.name, type: group.type });
    setShowAddModal(true);
  };

  const handleDeleteClick = (group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/groups/${groupToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showToast('Group deleted successfully', 'success');
      setShowDeleteModal(false);
      setGroupToDelete(null);
      fetchGroups();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete group', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchElections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const now = new Date();
      const active = (response.data.data || []).filter(e => {
        return e.accessType === 'closed' && e.status === 'active' && new Date(e.endDate) >= now;
      });
      setElections(active);
    } catch (error) {
      console.error('Failed to fetch elections:', error);
    }
  };

  const handleSendToGroup = async (group) => {
    setSendGroup(group);
    setSendElection('');
    setSendProgress(null);
    // Fetch members of this group
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allMembers = response.data.data || [];
      const inGroup = allMembers.filter(m => m.group && m.group.id === group.id && m.email && !m.email.endsWith('.votewave.internal'));
      setGroupMembers(inGroup);
    } catch {
      setGroupMembers([]);
    }
    setShowSendModal(true);
  };

  const handleSendGroupLinks = async () => {
    if (!sendElection || groupMembers.length === 0) return;
    setIsSending(true);
    setSendProgress({ sent: 0, total: groupMembers.length, failed: 0 });
    let sent = 0, failed = 0;
    const token = localStorage.getItem('token');
    for (const member of groupMembers) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/members/send-voting-link`,
          { memberId: member.id, electionId: sendElection },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        sent++;
      } catch { failed++; }
      setSendProgress({ sent: sent + failed, total: groupMembers.length, failed });
    }
    showToast(`Sent ${sent} link${sent !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} failed` : ''} to ${sendGroup.name}`, sent > 0 ? 'success' : 'error');
    setIsSending(false);
    setShowSendModal(false);
  };

  const getTypeIcon = (type) => type === 'class' ? 'bi-mortarboard' : 'bi-building';
  const getTypeBadgeStyle = (type) => ({
    background: type === 'class' ? '#4f46e5' : '#ea580c',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.7rem',
    textTransform: 'capitalize',
    border: 'none',
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
  });

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const classCount = groups.filter(g => g.type === 'class').length;
  const deptCount = groups.filter(g => g.type === 'department').length;
  const totalMembers = groups.reduce((sum, g) => sum + (g.memberCount || 0), 0);

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1" style={{ fontWeight: '600' }}>Groups</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Manage classes and departments for your organization
          </p>
        </div>
        <button
          className="btn btn-sm"
          onClick={() => {
            setEditingGroup(null);
            setFormData({ name: '', type: 'class' });
            setShowAddModal(true);
          }}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1.25rem',
            fontSize: '0.875rem',
            fontWeight: '500',
          }}
        >
          <i className="bi bi-plus-lg me-2"></i>Add Group
        </button>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        {[
          { title: 'Total Groups', value: groups.length, icon: 'bi-collection', color: '#667eea' },
          { title: 'Classes', value: classCount, icon: 'bi-mortarboard', color: '#0dcaf0' },
          { title: 'Departments', value: deptCount, icon: 'bi-building', color: '#f59e0b' },
          { title: 'Total Members', value: totalMembers, icon: 'bi-people', color: '#10b981' },
        ].map((card, i) => (
          <div key={i} className="col-lg-3 col-md-6 mb-3">
            <div className="card" style={{ border: 'none', borderLeft: `4px solid ${card.color}` }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted" style={{ fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {card.title}
                    </div>
                    <div className="h4 mb-0 mt-1" style={{ fontWeight: '700' }}>{card.value}</div>
                  </div>
                  <div style={{ fontSize: '2rem', color: card.color, opacity: 0.3 }}>
                    <i className={`bi ${card.icon}`}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Groups Table Card */}
      <div className="card" style={{ border: 'none' }}>
        <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>All Groups</h5>
          <div className="d-flex gap-2">
            {[
              { label: 'All', value: '' },
              { label: 'Classes', value: 'class', icon: 'bi-mortarboard' },
              { label: 'Departments', value: 'department', icon: 'bi-building' },
            ].map(f => (
              <button
                key={f.value}
                className={`btn btn-sm ${
                  filterType === f.value ? 'btn-primary' : 'btn-outline-secondary'
                }`}
                onClick={() => setFilterType(f.value)}
                style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
              >
                {f.icon && <i className={`bi ${f.icon} me-1`}></i>}
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="card-body p-0">
          {groups.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-collection" style={{ fontSize: '3rem', color: '#d1d5db' }}></i>
              <p className="mt-3 text-muted">
                {filterType === 'class'
                  ? 'No classes found. Create your first class.'
                  : filterType === 'department'
                  ? 'No departments found. Create your first department.'
                  : 'No groups found. Create your first class or department.'}
              </p>
            </div>
          ) : (
            groups.map((group, idx) => (
              <div
                key={group.id}
                className="d-flex justify-content-between align-items-center px-4 py-3"
                style={{
                  borderBottom: idx < groups.length - 1 ? '1px solid #f3f4f6' : 'none',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div className="d-flex align-items-center">
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px',
                    background: group.type === 'class' ? '#eef2ff' : '#fef3c7',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginRight: '1rem', flexShrink: 0,
                  }}>
                    <i className={`bi ${getTypeIcon(group.type)}`} style={{ color: group.type === 'class' ? '#4f46e5' : '#d97706', fontSize: '1.1rem' }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>{group.name}</div>
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <span className="badge" style={getTypeBadgeStyle(group.type)}>{group.type}</span>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        {group.memberCount || 0} member{(group.memberCount || 0) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {elections.length > 0 && (group.memberCount || 0) > 0 && (
                    <button
                      onClick={() => handleSendToGroup(group)}
                      className="btn btn-sm btn-light"
                      style={{ borderRadius: '8px', padding: '0.3rem 0.6rem' }}
                      title="Send voting links to this group"
                    >
                      <i className="bi bi-send" style={{ fontSize: '0.85rem', color: '#10b981' }}></i>
                    </button>
                  )}
                  <button
                    onClick={() => handleEditClick(group)}
                    className="btn btn-sm btn-light"
                    style={{ borderRadius: '8px', padding: '0.3rem 0.6rem' }}
                    title="Edit"
                  >
                    <i className="bi bi-pencil" style={{ fontSize: '0.85rem', color: '#6b7280' }}></i>
                  </button>
                  <button
                    onClick={() => handleDeleteClick(group)}
                    className="btn btn-sm btn-light"
                    style={{ borderRadius: '8px', padding: '0.3rem 0.6rem' }}
                    title="Delete"
                  >
                    <i className="bi bi-trash" style={{ fontSize: '0.85rem', color: '#dc3545' }}></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingGroup ? 'Edit Group' : 'Add Group'}</h5>
                <button className="btn-close" onClick={() => { setShowAddModal(false); setEditingGroup(null); }}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g. S4 Science, Finance Department"
                      required
                    />
                  </div>
                  {!editingGroup && (
                    <div className="mb-3">
                      <label className="form-label">Type</label>
                      <select
                        className="form-select"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      >
                        <option value="class">Class</option>
                        <option value="department">Department</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowAddModal(false); setEditingGroup(null); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : (editingGroup ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Send to Group Modal */}
      {showSendModal && sendGroup && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-send me-2" style={{ color: '#10b981' }}></i>
                  Send Links to {sendGroup.name}
                </h5>
                <button className="btn-close" onClick={() => !isSending && setShowSendModal(false)}></button>
              </div>
              <div className="modal-body">
                {groupMembers.length === 0 ? (
                  <div className="alert alert-warning" style={{ fontSize: '0.875rem' }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    No voters with email addresses found in this group. Only voters with real emails can receive voting links.
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Send voting links to <strong>{groupMembers.length}</strong> voter{groupMembers.length !== 1 ? 's' : ''} in <strong>{sendGroup.name}</strong>.
                    </p>
                    <div className="mb-3">
                      <label className="form-label">Select Election *</label>
                      <select className="form-select" value={sendElection} onChange={(e) => setSendElection(e.target.value)}>
                        <option value="">-- Choose an election --</option>
                        {elections.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </select>
                    </div>
                    {sendProgress && (
                      <div className="mt-3">
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.8rem' }}>
                          <span>Sending...</span>
                          <span>{sendProgress.sent} / {sendProgress.total}</span>
                        </div>
                        <div className="progress" style={{ height: '8px' }}>
                          <div className="progress-bar" style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%`, background: 'linear-gradient(90deg, #10b981, #059669)' }}></div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowSendModal(false)} disabled={isSending}>Cancel</button>
                {groupMembers.length > 0 && (
                  <button className="btn btn-success" onClick={handleSendGroupLinks} disabled={isSending || !sendElection}>
                    {isSending ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>Sending...</>
                    ) : (
                      <><i className="bi bi-send me-1"></i>Send to {groupMembers.length}</>          
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => { setShowDeleteModal(false); setGroupToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        title="Delete Group"
        message={`Are you sure you want to delete "${groupToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Groups;
